import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Modal from 'components/Modal';
import { IWaitingListResItem } from 'features/user/services/types/waiting';
import { Form } from 'antd';
import styled from 'styled-components';
import SandClockBox from './SandClockBox';
import PopoverCustomerInfo from './PopoverCustomerInfo';
import ChangeServiceModal, {
  ChangeServiceModalRef,
} from 'features/ticketList/pages/TicketDetailsPage/components/ChangeServiceModal';
import { IServiceItemData } from 'services/shop/types/categories';
import ChooseStaffSyncWaitingV2Form from './ChooseStaffSyncWaitingV2Form';
import { IStaffAvailableItem } from 'services/shop/types/staff';
import SquareCheckbox from './SquareCheckbox';
import AddButton from './AddButton';
import { IApiSyncWaitingToTicketV2 } from 'features/user/services/types/api';
import userApis from 'features/user/services/apis';
import toast from 'utils/toast';
import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import ServicesFormList from './ServicesFormList';
import { useAppDispatch } from 'store/hooks';
import paymentActions from 'features/payment/services/actions';
import Button from 'components/Button';
import Icon from 'assets/Icons';

type ModalSyncWaitingV2Ref = {
  open: (data: IWaitingListResItem, callback: (dataSuccess: any) => void) => void;
  close: () => void;
};

type Props = {};

export type TypeItemFormSyncWaitingV2 = {
  itemId: string;
  itemName: string;
  duration: number;
  staffId: string;
  staffName: string;
  select: boolean;
  uniqueId: string;
  staffAvatar: string;
};

type FormType = {
  items: TypeItemFormSyncWaitingV2[];
  validating?: boolean;
};

export const useModalSyncWaitingV2Ref = () => useRef<ModalSyncWaitingV2Ref>(null);
const ModalSyncWaitingV2 = forwardRef<ModalSyncWaitingV2Ref, Props>((props, ref) => {
  const [form] = Form.useForm<FormType>();
  const callBack = useRef<(dataSuccess: any) => void>(() => undefined);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<IWaitingListResItem>();
  const [isLoading, setIsLoading] = useState(false);
  const changeServiceModalRef = useRef<ChangeServiceModalRef>(null);
  const dispatch = useAppDispatch();

  const fetchSyncWaitingToTicketV2 = async (values: FormType, isPrint: boolean) => {
    if(values.validating) return;
    try {
      form.setFieldValue('validating', true);
      setIsLoading(true);
      const body: IApiSyncWaitingToTicketV2 = {
        items: values.items.map((item) => ({
          staffId: item.staffId,
          serviceId: item.itemId,
        })),
        checkInId: data?.checkInId || '',
      };
      const res = await userApis.syncWaitingToTicketV2(body);
      if (res.data.data) {
        toast.success('Sync waiting to ticket v2 success');
        callBack.current(res.data.data);
        if (isPrint) {
          dispatch(paymentActions.getInfoPrintWaiting.success(res.data.data));
        }
        handleClose();
      } else {
        toast.error('Sync waiting to ticket v2 failed');
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Sync waiting to ticket v2 failed');
    } finally {
      setIsLoading(false);
      form.setFieldValue('validating', false);
    }
  };

  const onSubmit = async (values: FormType) => {
    await fetchSyncWaitingToTicketV2(values, false);
  };

  const handlePrint = async () => {
    try {
      setIsLoading(true);
      await form.validateFields();
      await fetchSyncWaitingToTicketV2(form.getFieldsValue(), true);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      const initialData = {
        items:
          data?.serviceInfo?.map((o, index) => ({
            ...o,
            itemId: o.itemId,
            staffId: data?.staffId || '',
            staffName: data?.staffName || '',
            fieldName: index,
            staffAvatar: data?.avatar,
            uniqueId: uuidv4(),
          })) || [],
      };
      form.setFieldsValue(initialData);
    }
  }, [data, open]);

  useImperativeHandle(ref, () => ({
    open: (data, callback) => {
      setOpen(true);
      callBack.current = callback;
      setData(data);
    },

    close: () => setOpen(false),
  }));

  const handleClose = () => {
    form.resetFields();
    setOpen(false);
  };

  const handleSelectAll = (value: boolean) => {
    const list = form.getFieldValue('items') || [];
    if (value) {
      form.setFieldsValue({
        items: list?.map((item: any) => ({
          ...item,
          select: true,
        })),
      });
    } else {
      form.setFieldsValue({
        items: list?.map((item: any) => ({
          ...item,
          select: false,
        })),
      });
    }
  };

  return (
    <Modal
      v2
      visible={!!open}
      modalTitle='Still waiting'
      noneBodyStyle
      onClose={handleClose}
      width={'80vw'}
      disableOk={isLoading}
      footer={
        <>
          <Button onClick={handleClose} ntype='SECONDARY'>
            Close
          </Button>
          <Button
            onClick={handlePrint}
            ntype='DARK_BLUE'
            style={{ display: 'flex', alignItems: 'center', gap: '4px', flexDirection: 'row' }}
            iconElement={<Icon type='printWhite' />}
            disabled={isLoading}
          >
            Print
          </Button>
          <Button ntype='PRIMARY' onClick={() => form.submit()} disabled={isLoading}>
            Ok
          </Button>
        </>
      }
    >
      <Form layout='vertical' onFinish={onSubmit} form={form} name='sync-waiting-v2'>
        <Form.Item noStyle name={'validating'}/>
        <Container>
          <LeftContainer>
            <LeftWrapper>
              <CustomerInfoHeaderWrapper>
                <CustomerInfoContainer>
                  <CustomerInfoWrapper>
                    <TicketNumber>#{data?.ticketNumber}</TicketNumber>
                    <PopoverCustomerInfo
                      customerName={data?.customerName}
                      customerPhone={data?.customerPhone}
                    />
                    <SandClockBox time={data?.time} />
                  </CustomerInfoWrapper>
                </CustomerInfoContainer>
              </CustomerInfoHeaderWrapper>

              <FormListWrapper>
                <SelectServiceHeaderWrapper>
                  <SelectServiceHeaderText>Selected services</SelectServiceHeaderText>
                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => {
                      const items = getFieldValue('items') || [];
                      const isSelectAll =
                        items.length > 0 && items?.every((item: any) => item.select);
                      if (items.length === 0) {
                        return null;
                      }
                      return (
                        <SquareCheckbox
                          value={isSelectAll}
                          onClick={(value) => handleSelectAll(value)}
                          label='Select all'
                        />
                      );
                    }}
                  </Form.Item>
                </SelectServiceHeaderWrapper>

                <Form.List
                  name='items'
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (!value || value.length === 0) {
                          return Promise.reject(new Error('Please add at least one service'));
                        }
                      },
                    },
                  ]}
                >
                  {(fields, { remove, move }) => {
                    return (
                      <FormListContainer>
                        <ServicesFormList fields={fields} remove={remove} move={move} />
                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const items = getFieldValue('items') || [];
                            if (items.length === 0) {
                              return (
                                <span style={{ color: '#FF0000' }}>
                                  Please add at least one service
                                </span>
                              );
                            }
                            return null;
                          }}
                        </Form.Item>
                        <AddButton
                          onClick={() => {
                            const handleAddServices = (
                              _?: any,
                              __?: any,
                              ___?: any,
                              serviceObjects?: IServiceItemData[]
                            ) => {
                              const current = form.getFieldValue('items') || [];
                              const toAdd = serviceObjects?.map((s) => ({
                                staffId: '',
                                staffName: '',
                                itemId: s.id, // adjust key as needed
                                itemName: s.serviceName,
                                duration: s.duration,
                                uniqueId: uuidv4(),
                              }));
                              const merged = [...current, ...(toAdd || [])];
                              form.setFieldsValue({ items: merged });
                            };

                            // open modal and pass the multi-add handler
                            changeServiceModalRef.current?.open(handleAddServices);
                          }}
                        />
                      </FormListContainer>
                    );
                  }}
                </Form.List>
              </FormListWrapper>
            </LeftWrapper>
          </LeftContainer>
          <RightContainer>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue, setFieldsValue }) => {
                const items = getFieldValue('items') || [];
                const selectedStaffIds =
                  items?.map((item: TypeItemFormSyncWaitingV2) => item.staffId) || [];
                const selectedServiceIds =
                  items
                    ?.filter((item: TypeItemFormSyncWaitingV2) => item?.select)
                    ?.map((item: TypeItemFormSyncWaitingV2) => item.uniqueId) || [];

                const onSelectStaff = (staff: IStaffAvailableItem) => {
                  setFieldsValue({
                    items: items?.map((item: TypeItemFormSyncWaitingV2) =>
                      selectedServiceIds.includes(item.uniqueId)
                        ? {
                            ...item,
                            staffId: staff.staffId,
                            staffName: staff.staffName,
                            duration: item.duration,
                            staffAvatar: staff.staffAvatar,
                            select: false,
                          }
                        : item
                    ),
                  });
                };

                return (
                  <ChooseStaffSyncWaitingV2Form
                    selectedStaffIds={selectedStaffIds}
                    onSelectStaff={onSelectStaff}
                  />
                );
              }}
            </Form.Item>
          </RightContainer>
        </Container>

        <ChangeServiceModal ref={changeServiceModalRef} modalTitle='Add service' isMultiple />
      </Form>
    </Modal>
  );
});
ModalSyncWaitingV2.displayName = 'ModalSyncWaitingV2';

export default ModalSyncWaitingV2;

const Container = styled.div`
  display: flex;
  min-height: 50vh;
`;

const RightContainer = styled.div`
  width: 100%;
  padding: 16px 16px 0 16px;
`;

const LeftContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  background: #f6f7fc;
  box-shadow: 0 4px 4px 0 rgba(45, 103, 114, 0.25);
`;

const LeftWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
`;

const CustomerInfoHeaderWrapper = styled.div`
  padding: 16px;
`;

const CustomerInfoContainer = styled.div`
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  border-radius: 4px;
  background: #fff;
`;

const FormListWrapper = styled.div``;

const CustomerInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const TicketNumber = styled.div`
  display: flex;
  padding: 4px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 4px;
  background: #232f3e;
  color: #fff;
  font-size: 36px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const SelectServiceHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 0 16px;
`;

const SelectServiceHeaderText = styled.div`
  color: #505050;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const FormListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: calc(80vh - 162px);
  padding: 16px;
  overflow-y: auto;

  .form-item-special {
    margin-bottom: 0;
    .ant-form-item-control-input {
      min-height: 0;
    }
  }
`;
